import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { cva } from "class-variance-authority";
import { ChevronDown } from "lucide-react";

import { Context as IntlContext } from "context/Intl";
import { MegaMenuHoverAnchor } from "./mega-menu";
import { ROUTES } from "routes";
import { cn } from "lib/utils";

import { useServicesMenuLabels } from "./useServicesMenuLabels";

const servicesNavLinkVariants = cva(
    "relative inline-flex items-center gap-1 rounded-md py-2 pl-2 pr-1 text-xs font-bold uppercase tracking-wide transition-colors sm:pl-3",
    {
        variants: {
            active: {
                true: "text-primary after:absolute after:bottom-0 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-primary",
                false: "text-foreground/80 hover:text-foreground",
            },
        },
        defaultVariants: { active: false },
    },
);

const servicesNavChevronVariants = cva(
    "inline-flex items-center rounded-md p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    {
        variants: {
            active: {
                true: "text-primary",
                false: "text-foreground/60 hover:text-foreground",
            },
        },
        defaultVariants: { active: false },
    },
);

interface ServicesMenuDesktopProps {
    label: string;
    isActive: boolean;
}

export const ServicesMenuDesktop: React.FC<ServicesMenuDesktopProps> = ({ label, isActive }) => {
    const intl = useContext(IntlContext);
    const { categories, panelLabels } = useServicesMenuLabels();

    return (
        <MegaMenuHoverAnchor
            categories={categories}
            labels={panelLabels}
            trigger={({ open }) => (
                <>
                    <Link to={ROUTES.PROVIDERS} className={cn(servicesNavLinkVariants({ active: isActive }))}>
                        {label}
                    </Link>
                    <button
                        type="button"
                        className={cn(servicesNavChevronVariants({ active: isActive }))}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label={`${label} — ${open ? panelLabels.viewAllLabel : intl.formatMessage("menu.services_open_menu")}`}
                    >
                        <ChevronDown
                            className={cn("h-3 w-3 transition-transform duration-200", open && "rotate-180")}
                            aria-hidden
                        />
                    </button>
                </>
            )}
        />
    );
};
